import React from 'react'
import { Crosshair } from 'lucide-react'
import { LengthUnits, lengthStep, roundLength } from '../types'
import { NumberInput, Slider, ToolButton } from './ui'

interface SplitHeightPanelProps {
  /** Part being cut. */
  partId: string
  /** Cut height and the part's own extent, all in model units. */
  height: number
  min: number
  max: number
  /** Display unit, and the factor from model units into it. */
  units: LengthUnits
  onHeightChange: (height: number) => void
  pickMode: boolean
  onTogglePick: () => void
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Cut-height control for splitting a two-tone part in two.
 *
 * Floats over the viewport rather than living in the inspector: while it is open a
 * guide plane is drawn on the model, and the choice being made is about the model
 * rather than about a material.
 */
export const SplitHeightPanel: React.FC<SplitHeightPanelProps> = ({
  partId,
  height,
  min,
  max,
  units,
  onHeightChange,
  pickMode,
  onTogglePick,
  onConfirm,
  onCancel,
}) => {
  const { unit, scale } = units
  const clamp = (v: number) => Math.min(max, Math.max(min, v))

  return (
    <div className="absolute z-20 top-md left-1/2 -translate-x-1/2 w-[440px] bg-white border border-gray-200 rounded-lg shadow-box p-md">
      <div className="flex items-center justify-between gap-sm mb-sm">
        <span className="text-title-sm text-gray-900">
          Split <span className="font-mono text-primary-700">{partId}</span>
        </span>
        <ToolButton
          onClick={onTogglePick}
          active={pickMode}
          icon={<Crosshair size={13} />}
          title="Click the model where the colour changes"
        >
          {pickMode ? 'Click the model…' : 'Pick the line'}
        </ToolButton>
      </div>

      <div className="flex items-center gap-sm">
        <Slider
          min={min * scale}
          max={max * scale}
          step={lengthStep(unit) / 2}
          value={height * scale}
          onChange={(e) => onHeightChange(parseFloat(e.target.value) / scale)}
        />
        <NumberInput
          step={lengthStep(unit)}
          value={roundLength(height * scale, unit)}
          onChange={(e) => {
            const v = parseFloat(e.target.value)
            if (Number.isFinite(v)) onHeightChange(clamp(v / scale))
          }}
          className="!w-[88px]"
        />
        <span className="text-text-sm text-gray-400">{unit}</span>
      </div>

      <div className="flex items-center justify-between gap-md mt-md">
        <span className="text-text-xs text-gray-400">
          Two parts, each with its own material — so an upper band can be true metal.
        </span>
        <div className="flex gap-sm">
          <ToolButton onClick={onCancel}>Cancel</ToolButton>
          <ToolButton onClick={onConfirm} variant="primary">
            Split
          </ToolButton>
        </div>
      </div>
    </div>
  )
}
